// i18n area 'objectStorage' (OBJ-1, 2026-08-04 — object storage S3 native on
// Ceph, docs/object-storage-s3-design-2026-08-04.md + ratifica §2): the Object
// Storage page — store list with per-node gateway census, the bucket and
// account subresource tabs, and the ONE-TIME key-pair render of account
// create/rotate (the M4c recovery-keys idiom: shown once, never again — the
// copy says so because the platform truly has nothing to show back). The nav
// label lives HERE (`objectStorage.nav`, the monitoring.nav precedent):
// dictionaries.ts composes sections with a shallow spread, so a second `nav`
// branch would clobber common.ts instead of merging into it.

export const en = {
  objectStorage: {
    nav: 'Object Storage',
    title: 'Object Stores',
    createTitle: 'Create object store',
    name: 'Name',
    nameHint:
      'Lowercase letters, digits and dashes — cosmetic: the S3 wire addresses node:port endpoints, never this name',
    datapool: 'Cluster',
    datapoolHint:
      "Serving storage clusters — one object store per cluster (Ceph: the store is the pool's RGW zone; MultiSCSI: it owns a backing volume)",
    noDatapools:
      'No serving cluster is free — create one from the Storage clusters page (or note each already holds its object store).',
    backing: 'Backing',
    backingHint:
      'A volume (RGW on ceph, versitygw on multiscsi) or a CephFS share (bidirectional: files are objects and objects are files)',
    backingVolume: 'Volume',
    backingShare: 'CephFS share (bidirectional)',
    share: 'File share',
    shareHint:
      'A serving, writable CephFS share without an object store — its top-level directories become the buckets; no versioning, Object Lock or quota on them',
    fileOwnerUid: 'File owner uid',
    fileOwnerGid: 'File owner gid',
    fileOwnerHint:
      "The one uid:gid files born via S3 get (empty = nobody 65534:65534 — set the NFS clients' ids for full two-way access)",
    volume: 'Volume',
    volumeHint:
      "The declared volume this store consumes — one store per volume; capacity and redundancy are the volume's declaration (Ceph: the store's RGW pools are the volume's; MultiSCSI: its filesystem is)",
    noVolumes:
      "No consumable volume — declare one from the Volumes page first (a declared volume is free; the store's creation materializes it as object storage).",

    // #181 ondata 5: the OBJ-MS webui seam (dated 2026-08-05) closes — the
    // fence text stops pointing at the CLI.
    engineFence:
      'S3 serves natively on BOTH engines (same TVStor S3 Profile): Ceph — one radosgw per member node, active/active; MultiSCSI — one versitygw gateway on the authority of an owned backing volume, active/passive behind a floating service address. Pick the cluster: its kind decides which halves this form asks for.',
    multiscsiHalvesHint:
      "MultiSCSI halves (required here, forbidden on Ceph): the owned backing volume's thin size and mirror policy — both IMMUTABLE after creation — and the floating service address S3 clients connect to (it follows the volume's authority).",
    sizeGiB: 'Size (GiB)',
    sizeGiBHint: "The owned backing volume's thin size — immutable (store expand is a later round)",
    copies: 'Mirror copies',
    minInSync: 'Min in sync',
    minInSyncHint: 'At least 1, at most the copies',
    serviceAddress: 'Service address',
    serviceAddressHint:
      'IPv4 or IPv6 literal, no prefix — must not collide with node, share or binding addresses; with a client Network declared it must fall inside a declared subnet',
    port: 'Port',
    profile: 'S3 profile',
    profileSummary: 'TVStor S3 Profile v{version} — {count} operations promised, identical on both engines',
    profileOut: 'out of profile (refused on both engines)',
    profileVerdicts:
      'On this engine ({engine}): {measured} of {total} measured on the real daemon in CI, {declared} declared',
    profileLimits:
      'Limits: {maxBuckets} buckets per account (explicit ceiling), more than {bucketCount} promised, objects up to {objectTb} TB via multipart (bench measure)',
    portHint: 'One TCP port, every gateway node — 9443 is the documented convention',
    tls: 'TLS certificate',
    tlsClusterOption: 'Cluster certificate (fallback)',
    tlsClusterBadge: 'Cluster',
    tlsCustomBadge: 'Custom',
    tlsHint:
      'Optional keystore certificate the data endpoint presents; without one the store serves with the CLUSTER certificate — S3 clients must then trust or pin the cluster CA (see the operator doc).',
    noTlsCerts:
      'No TLS certificate in the keystore yet — upload one from the Credentials page (kind TLS certificate), or keep the cluster-certificate fallback.',
    backupDestinationNote:
      "An object store is a supported backup destination for another TVStor cluster (A→B). Pointing THIS cluster's own backups at it is permitted too — that protects from ransomware and the rogue admin, NOT from the cluster's own disaster.",
    endpoints: 'Endpoints',

    // #181 §3.2: the page is bi-engine since OBJ-MS — the copy stopped
    // pretending every store is ceph.
    activeActive:
      "Ceph: active/active on the member nodes' data addresses — MultiSCSI: active/passive behind the floating service address",
    usage: 'Usage',
    bucketCount: 'Buckets (observed)',
    objectCount: 'Objects',
    usedBytes: 'Used',
    phase: 'Phase',
    phases: {
      pending: 'Pending',
      creating: 'Creating',
      ready: 'Ready',
      degraded: 'Degraded',
      error: 'Error',
      deleting: 'Deleting'
    },
    notObserved: 'Not yet observed',
    observedAt: 'Observed at',
    tabOverview: 'Overview',
    tabGateways: 'Gateways',
    tabBuckets: 'Buckets',
    tabAccounts: 'S3 Accounts',

    // ENEA-B-R14 (2026-10-24): the store's QoS (RGW rate limit per account; bytes on a multiscsi store's address).
    tabQos: 'QoS',
    qos: {
      intro:
        'Ceilings of the store, opt-in (empty = no limit; floors 1 op/s and 1 MiB/s). On RGW they are the rate limit of EVERY account of the store (global ratelimit, user scope) and a bucket can override them (bucket QoS): RGW counts per ONE-MINUTE window and per radosgw instance — a client over the ceiling gets 503 SlowDown. On a multiscsi store (versitygw) only the throughput axes apply, shaped on the store’s service address.',
      readOps: 'Read ops/s per account',
      writeOps: 'Write ops/s per account',
      ceilings: 'Ceilings',
      save: 'Save ceilings',
      saved: 'Ceilings saved — the gateway converges on its next pass (no restart).',
      current: 'Current',
      bidirectionalHint:
        'A bidirectional store (versitygw over a CephFS share) serves from the cluster’s gateway addresses and versitygw has no rate limiting: no ceiling applies here (422 qos_not_supported) — set the cluster’s gatewayQos per gateway address.',
      versitygwHint:
        'versitygw has no request rate limiting: only the throughput ceilings apply on this store (shaped on its service address); IOPS are the volume’s QoS.',
      status: {
        title: 'Applied by the gateway',
        none: 'not reported yet',
        shaped: 'shaped on',
        notShaped: 'NOT shaped on',
        rgwApplied: 'applied per account',
        rgwNotApplied: 'not applied by the gateway',
        windowPerInstance: '1-minute window, per instance',
        observedAt: 'Observed',
        hint: 'What the gateway read back (ratelimit get / tc), never the wish; on RGW the ceiling counts per one-minute window and per radosgw instance (with N gateways the aggregate is up to N×).'
      }
    },

    // ENEA-A-R12 (2026-10-23): the store's S3 client audit.
    tabAudit: 'Audit',
    audit: {
      intro:
        'The S3 client audit records every request the store serves — user (access key), client address, operation, bucket/key (never content), outcome, bytes — in the ONE event form shared with the shares: on the RGW engine through the ops log (the radosgw restarts on the change), on versitygw through its access log. Events land in the serving node’s audit file, rotate on their own cap, reach every syslog sink that forwards daemon logs and feed the objectstore.* audit metrics and the factory rules.',
      policy: {
        title: 'Policy',
        enabled: 'Client audit',
        on: 'On',
        off: 'Off (default)',
        operations: 'Operations recorded',
        operationsHint:
          'None selected = every request. Authentication failures and denied requests are recorded regardless (security).',
        costHint:
          'Off by default: the ops log costs per request. With the audit on over a busy store the syslog collector is where the stream lives; the local file is a queue.',
        save: 'Save policy',
        saved: 'Audit policy saved — the gateways converge on their next pass'
      },
      operations: {
        auth: 'Authentication',
        read: 'Read (GET object)',
        write: 'Write (PUT object)',
        create: 'Create (bucket / multipart)',
        delete: 'Delete',
        list: 'List',
        head: 'Head',
        copy: 'Copy',
        setacl: 'Set ACL / policy'
      }
    },
    gateways: {
      daemonsTitle: 'Gateway daemons (observed — radosgw on Ceph, versitygw on MultiSCSI)',
      endpointsTitle: 'Data endpoints',
      node: 'Node',
      state: 'State',
      pid: 'PID',
      version: 'Version',
      observedAt: 'Observed at',
      empty: 'No gateway observations yet',
      url: 'URL',
      healthy: 'Healthy',
      unhealthy: 'Unreachable',
      notProbed: 'Not yet probed'
    },
    delete: {
      title: 'Delete object store {name}?',

      // #181 T6: the multiscsi half was UNSAID — on a multiscsi store the
      // delete DESTROYS the owned backing volume with the store (contract
      // objectstores.yaml: legs, snapshots, rows; no volume-fate parameter —
      // the emptiness gate guarantees no user objects remain).
      warning:
        "The gateways stop. On a Ceph store the RGW zone and its RADOS pools are removed; on a MultiSCSI store the service address is released and the owned backing volume is DESTROYED with the store (legs, snapshots, rows — no keep option: the emptiness gate below guarantees it holds no user objects). A store still holding buckets — managed OR observed unmanaged — is refused: empty it first. The store's S3 accounts die with it, minted credentials included.",
      busyBlock: 'A task is already moving this store'
    },
    buckets: {
      title: 'S3 buckets (managed)',
      createTitle: 'Create bucket',
      name: 'Bucket name',
      nameHint: '3–63 lowercase letters, digits and hyphens; no dots, not IPv4-shaped — immutable: S3 has no rename',
      owner: 'Owner account',
      ownerHint: 'The S3 account with full control on the wire — an account of this store',
      noAccounts: 'No ready S3 account on this store yet — create one in the S3 Accounts tab first.',
      quota: 'Quota',
      quotaGiB: 'Quota (GiB)',
      quotaHint:
        'Optional; empty = unlimited. Ceph: enforced natively by RGW. MultiSCSI: a quota is REFUSED for now (enforcement unverified — honest refusal over fake enforcement, dated seam)',
      unlimited: 'Unlimited',

      // #181 ondata 5 — Profile v1 at birth and by PATCH: versioning (S3
      // transitions only) and Object Lock (creation-only presence).
      versioningLabel: 'Versioning',
      versioningHint:
        'Enabled at birth: every overwrite mints a version, delete becomes a delete marker. It can be suspended later (kept versions stay) — back to never does not exist.',
      objectLockLabel: 'Object Lock (WORM)',
      objectLockHint:
        "SET AT CREATION ONLY (AWS semantics): a bucket not born locked can never be locked. Requires versioning; a locked bucket can never suspend it. Compliance retention is the engine's hard denial until expiry; governance admits the privileged bypass — enforcement strength differs per engine, declared.",

      // ENEA-B-R14: the bucket's own ceilings (RGW rate limit per bucket).
      qos: 'Bucket QoS',
      qosHint:
        'An override of the store’s per-account ceiling for this bucket (RGW ratelimit, bucket scope; same one-minute window). Empty = inherit. On other engines it is 422 qos_not_supported.',
      qosReadOps: 'Read ops/s',
      qosWriteOps: 'Write ops/s',
      qosReadBps: 'Read MiB/s',
      qosWriteBps: 'Write MiB/s',
      retentionMode: 'Default retention',
      retentionModeHint: 'Optional default applied to new object versions carrying none of their own',
      retentionNone: 'No default — objects lock only when a client sets retention explicitly',
      retentionGovernance: 'Governance (privileged bypass possible)',
      retentionCompliance: 'Compliance (no bypass until expiry)',
      retentionAmount: 'Duration',
      retentionUnit: 'Unit',
      retentionDays: 'days',
      retentionYears: 'years',
      editTitle: 'Edit bucket {name}',
      quotaEditHint: 'Empty = remove the quota (unlimited). Ceph only — MultiSCSI refuses a quota (dated seam)',
      versioningEditHint:
        'S3 transitions only: never→enabled, enabled→suspended, suspended→enabled — back to never does not exist on the wire',
      versioningLockedHint: 'This bucket is lock-enabled: versioning can never be suspended (the AWS rule)',
      versioningNever: 'Never enabled',
      versioningStates: {
        enabled: 'Enabled',
        suspended: 'Suspended (kept versions stay)'
      },
      versioningBadge: 'versioned',
      versioningSuspendedBadge: 'versioning suspended',
      lockBadge: 'LOCK',
      lockNoDefault: 'Lock-enabled, no default retention — objects lock only when a client sets retention explicitly',
      retentionEditHint: 'Add, change or remove the default — the lock presence itself is a birth property',
      usage: 'Usage',
      objectsSuffix: 'objects',
      empty: 'No managed buckets yet',
      wireNote:
        'Account holders may also create buckets over the S3 wire — the platform does not force exclusivity: protocol-born buckets show below as unmanaged, never as rows of ours.',
      delete: {
        title: 'Delete bucket {name}?',
        warning:
          'The bucket leaves the zone — and the zone namespace stays shared: an S3 client may re-create the name afterwards.',
        deleteObjectsLabel: 'Also purge the objects it contains',
        purgeWarning: 'The task PURGES every object with the bucket — unrecoverable.',
        keepHint:
          'Only an EMPTY bucket is deleted — one whose census shows objects is refused, and the executor re-checks against the live zone.'
      },
      unmanagedTitle: 'Unmanaged buckets (observed)',
      unmanagedHint:
        "Born over the S3 wire by an account holder — observed honestly, never owned: manage or delete them with an S3 client holding the owner's keys.",
      unmanagedOwner: 'Owner (gateway uid)'
    },
    accounts: {
      title: 'S3 accounts',
      createTitle: 'Create S3 account',
      name: 'Name',
      nameHint: 'Lowercase letters, digits and dashes — immutable: the gateway identity is minted from it',
      accessKeyId: 'Access key ID',
      notMinted: 'Not yet minted',
      empty: 'No S3 accounts yet',
      createHint:
        'The platform mints the key pair and seals it write-only into the keystore — the secret renders ONCE, in the next screen, and never again.',
      secretTitle: 'Key pair — shown once',
      secretAccessKey: 'Secret access key',
      secretShownOnce:
        'Shown once — the keystore holds it write-only and nothing on the platform will ever display it again. Losing it means Rotate.',
      rotate: 'Rotate key pair',
      rotateTitle: 'Rotate the key pair of {name}',
      rotateHint:
        'Mints a NEW pair — access key id AND secret — rendered once in the next screen. New-before-old: the old key keeps authenticating for the grace window, then a child task retires it.',
      graceMinutes: 'Grace window (minutes)',
      graceHint: '0–1440 — how long the OLD key keeps working after the new one is live (0 = immediate swap)',
      delete: {
        title: 'Delete S3 account {name}?',
        warning:
          'The gateway identity goes and its keys stop authenticating immediately; the minted keystore credential is reaped with it. An account still owning managed buckets is refused — delete those buckets first.'
      }
    }
  }
}

export const it: typeof en = {
  objectStorage: {
    nav: 'Storage a oggetti',
    title: 'Object store',
    createTitle: 'Crea object store',
    name: 'Nome',
    nameHint: 'Minuscole, cifre e trattini — cosmetico: il filo S3 indirizza gli endpoint nodo:porta, mai questo nome',
    datapool: 'Cluster',
    datapoolHint:
      'Cluster di storage in servizio — un object store per cluster (Ceph: lo store è la zona RGW del pool; MultiSCSI: possiede un volume di backing)',
    noDatapools:
      'Nessun cluster in servizio è libero — creane uno dalla pagina Cluster di storage (o nota che ognuno ha già il suo object store).',
    backing: 'Base',
    backingHint:
      'Un volume (RGW su ceph, versitygw su multiscsi) o una share CephFS (bidirezionale: i file sono oggetti e gli oggetti sono file)',
    backingVolume: 'Volume',
    backingShare: 'Share CephFS (bidirezionale)',
    share: 'File share',
    shareHint:
      'Una share CephFS in servizio, scrivibile e senza object store — le sue directory di primo livello diventano i bucket; niente versioning, Object Lock o quota su di essi',
    fileOwnerUid: 'uid proprietario dei file',
    fileOwnerGid: 'gid proprietario dei file',
    fileOwnerHint:
      "L'unico uid:gid dei file nati via S3 (vuoto = nobody 65534:65534 — imposta gli id dei client NFS per l'accesso pieno nei due versi)",
    volume: 'Volume',
    volumeHint:
      'Il volume dichiarato che questo store consuma — uno store per volume; capacità e ridondanza sono la dichiarazione del volume (Ceph: i pool RGW dello store sono del volume; MultiSCSI: lo è il suo filesystem)',
    noVolumes:
      'Nessun volume consumabile — dichiarane uno dalla pagina Volumi (un volume dichiarato è libero; la creazione dello store lo materializza come object storage).',
    engineFence:
      "S3 serve nativamente su ENTRAMBI i motori (stesso TVStor S3 Profile): Ceph — un radosgw per nodo membro, attivo/attivo; MultiSCSI — un gateway versitygw sull'authority di un volume di backing posseduto, attivo/passivo dietro un indirizzo di servizio flottante. Scegli il cluster: la sua specie decide quali metà chiede questo modulo.",
    multiscsiHalvesHint:
      "Metà MultiSCSI (richieste qui, vietate su Ceph): la taglia thin del volume di backing posseduto e la sua politica mirror — entrambe IMMUTABILI dopo la creazione — e l'indirizzo di servizio flottante a cui i client S3 si connettono (segue l'authority del volume).",
    sizeGiB: 'Taglia (GiB)',
    sizeGiBHint:
      "La taglia thin del volume di backing posseduto — immutabile (l'espansione dello store è un round successivo)",
    copies: 'Copie mirror',
    minInSync: 'Minimo in sync',
    minInSyncHint: 'Almeno 1, al massimo le copie',
    serviceAddress: 'Indirizzo di servizio',
    serviceAddressHint:
      'Letterale IPv4 o IPv6, senza prefisso — non deve collidere con indirizzi di nodi, share o binding; con una Network client dichiarata deve cadere in una subnet dichiarata',
    port: 'Porta',
    profile: 'Profilo S3',
    profileSummary: 'TVStor S3 Profile v{version} — {count} operazioni promesse, identiche sui due motori',
    profileOut: 'fuori profilo (rifiutate su entrambi i motori)',
    profileVerdicts:
      'Su questo motore ({engine}): {measured} su {total} misurate sul demone vero in CI, {declared} dichiarate',
    profileLimits:
      'Limiti: {maxBuckets} bucket per account (tetto esplicito), più di {bucketCount} promessi, oggetti fino a {objectTb} TB via multipart (misura di banco)',
    portHint: 'Una porta TCP, ogni nodo gateway — 9443 è la convenzione documentata',
    tls: 'Certificato TLS',
    tlsClusterOption: 'Certificato di cluster (fallback)',
    tlsClusterBadge: 'Cluster',
    tlsCustomBadge: 'Custom',
    tlsHint:
      "Certificato opzionale dal keystore presentato dall'endpoint dati; senza, lo store serve col certificato di CLUSTER — i client S3 devono allora fidarsi della CA del cluster o fissarla (vedi il manuale operatore).",
    noTlsCerts:
      'Nessun certificato TLS nel keystore — caricane uno dalla pagina Credenziali (tipo Certificato TLS), o tieni il fallback al certificato di cluster.',
    backupDestinationNote:
      "Un object store è una destinazione backup supportata per un altro cluster TVStor (A→B). Puntarci anche i backup di QUESTO stesso cluster è permesso — protegge dal ransomware e dall'amministratore infedele, NON dal disastro del cluster stesso.",
    endpoints: 'Endpoint',
    activeActive:
      "Ceph: attivo/attivo sugli indirizzi dati dei nodi membri — MultiSCSI: attivo/passivo dietro l'indirizzo di servizio flottante",
    usage: 'Utilizzo',
    bucketCount: 'Bucket (osservati)',
    objectCount: 'Oggetti',
    usedBytes: 'Usato',
    phase: 'Fase',
    phases: {
      pending: 'In attesa',
      creating: 'In creazione',
      ready: 'Pronto',
      degraded: 'Degradato',
      error: 'Errore',
      deleting: 'In eliminazione'
    },
    notObserved: 'Non ancora osservato',
    observedAt: 'Osservato alle',
    tabOverview: 'Panoramica',
    tabGateways: 'Gateway',
    tabBuckets: 'Bucket',
    tabAccounts: 'Account S3',

    // ENEA-B-R14 (2026-10-24): la QoS dello store (rate limit RGW per account; byte sull'indirizzo di uno store multiscsi).
    tabQos: 'QoS',
    qos: {
      intro:
        'Tetti dello store, opt-in (vuoto = nessun limite; pavimenti 1 op/s e 1 MiB/s). Su RGW sono il rate limit di OGNI account dello store (global ratelimit, scope user) e un bucket può sovrascriverli (QoS del bucket): RGW conta per finestra di UN MINUTO e per istanza radosgw — un client sopra il tetto riceve 503 SlowDown. Su uno store multiscsi (versitygw) valgono solo gli assi di banda, modellati sull’indirizzo di servizio dello store.',
      readOps: 'Letture op/s per account',
      writeOps: 'Scritture op/s per account',
      ceilings: 'Tetti',
      save: 'Salva i tetti',
      saved: 'Tetti salvati — il gateway converge alla prossima passata (nessun riavvio).',
      current: 'Attuale',
      bidirectionalHint:
        'Uno store bidirezionale (versitygw su una share CephFS) serve dagli indirizzi di gateway del cluster e versitygw non ha un rate limit: nessun tetto qui (422 qos_not_supported) — imposta il gatewayQos del cluster per indirizzo di gateway.',
      versitygwHint:
        'versitygw non ha un rate limit di richieste: su questo store valgono solo i tetti di banda (modellati sul suo indirizzo di servizio); gli IOPS sono la QoS del volume.',
      status: {
        title: 'Applicato dal gateway',
        none: 'non ancora riportato',
        shaped: 'modellato su',
        notShaped: 'NON modellato su',
        rgwApplied: 'applicato per account',
        rgwNotApplied: 'non applicato dal gateway',
        windowPerInstance: 'finestra di 1 minuto, per istanza',
        observedAt: 'Osservato',
        hint: 'Ciò che il gateway ha riletto (ratelimit get / tc), mai il desiderio; su RGW il tetto conta per finestra di un minuto e per istanza radosgw (con N gateway l’aggregato è fino a N×).'
      }
    },
    tabAudit: 'Audit',
    audit: {
      intro:
        'L’audit client S3 registra ogni richiesta servita dallo store — utente (access key), indirizzo del client, operazione, bucket/chiave (mai il contenuto), esito, byte — nell’UNICA forma di evento condivisa con le share: sul motore RGW tramite l’ops log (il radosgw si riavvia al cambio), su versitygw tramite il suo access log. Gli eventi finiscono nel file di audit del nodo servente, ruotano con il loro tetto, arrivano a ogni sink syslog che inoltra i log dei demoni e alimentano le metriche objectstore.* dell’audit e le regole di fabbrica.',
      policy: {
        title: 'Policy',
        enabled: 'Audit client',
        on: 'Acceso',
        off: 'Spento (default)',
        operations: 'Operazioni registrate',
        operationsHint:
          'Nessuna selezionata = tutte le richieste. Autenticazioni fallite e richieste negate si registrano comunque (sicurezza).',
        costHint:
          'Spento di default: l’ops log costa per richiesta. Con l’audit acceso su store molto trafficati, il collettore syslog è il posto dove il flusso vive; il file locale è una coda.',
        save: 'Salva policy',
        saved: 'Policy di audit salvata — i gateway convergono al passaggio successivo'
      },
      operations: {
        auth: 'Autenticazione',
        read: 'Lettura (GET object)',
        write: 'Scrittura (PUT object)',
        create: 'Creazione (bucket / multipart)',
        delete: 'Cancellazione',
        list: 'Elenco',
        head: 'Head',
        copy: 'Copia',
        setacl: 'ACL / policy'
      }
    },
    gateways: {
      daemonsTitle: 'Demoni gateway (osservati — radosgw su Ceph, versitygw su MultiSCSI)',
      endpointsTitle: 'Endpoint dati',
      node: 'Nodo',
      state: 'Stato',
      pid: 'PID',
      version: 'Versione',
      observedAt: 'Osservato alle',
      empty: 'Ancora nessuna osservazione dei gateway',
      url: 'URL',
      healthy: 'Sano',
      unhealthy: 'Irraggiungibile',
      notProbed: 'Non ancora sondato'
    },
    delete: {
      title: "Eliminare l'object store {name}?",
      warning:
        "I gateway si fermano. Su uno store Ceph vengono rimossi la zona RGW e i suoi pool RADOS; su uno store MultiSCSI l'indirizzo di servizio viene rilasciato e il volume di backing posseduto viene DISTRUTTO insieme allo store (gambe, snapshot, righe — nessuna opzione per tenerlo: il cancello di vuotezza qui sotto garantisce che non contenga oggetti utente). Uno store che contiene ancora bucket — gestiti O osservati non gestiti — viene rifiutato: svuotalo prima. Gli account S3 dello store muoiono con lui, credenziali coniate incluse.",
      busyBlock: 'Un task sta già muovendo questo store'
    },
    buckets: {
      title: 'Bucket S3 (gestiti)',
      createTitle: 'Crea bucket',
      name: 'Nome del bucket',
      nameHint:
        '3–63 tra minuscole, cifre e trattini; niente punti, non a forma di IPv4 — immutabile: S3 non ha il rename',
      owner: 'Account proprietario',
      ownerHint: "L'account S3 con pieno controllo sul filo — un account di questo store",
      noAccounts: 'Nessun account S3 pronto su questo store — creane uno nel tab Account S3.',
      quota: 'Quota',
      quotaGiB: 'Quota (GiB)',
      quotaHint:
        "Opzionale; vuota = illimitata. Ceph: imposta nativamente da RGW. MultiSCSI: per ora una quota viene RIFIUTATA (enforcement non verificato — rifiuto onesto invece di un'imposizione finta, seam datato)",
      unlimited: 'Illimitata',
      versioningLabel: 'Versioning',
      versioningHint:
        'Attivo dalla nascita: ogni sovrascrittura conia una versione, il delete diventa un delete marker. Si può sospendere dopo (le versioni esistenti restano) — il ritorno a mai-attivo non esiste.',
      objectLockLabel: 'Object Lock (WORM)',
      objectLockHint:
        "SOLO ALLA CREAZIONE (semantica AWS): un bucket non nato bloccato non potrà mai esserlo. Richiede il versioning; un bucket bloccato non può mai sospenderlo. La retention compliance è il rifiuto duro del motore fino alla scadenza; governance ammette il bypass privilegiato — la forza dell'enforcement cambia per motore, dichiarato.",

      // ENEA-B-R14: i tetti propri del bucket (rate limit RGW per bucket).
      qos: 'QoS del bucket',
      qosHint:
        'Un override del tetto per account dello store per questo bucket (ratelimit RGW, scope bucket; stessa finestra di un minuto). Vuoto = eredita. Sugli altri motori è 422 qos_not_supported.',
      qosReadOps: 'Letture op/s',
      qosWriteOps: 'Scritture op/s',
      qosReadBps: 'Lettura MiB/s',
      qosWriteBps: 'Scrittura MiB/s',
      retentionMode: 'Retention di default',
      retentionModeHint: 'Default opzionale applicato alle nuove versioni di oggetto che non ne portano una propria',
      retentionNone:
        'Nessun default — gli oggetti si bloccano solo quando un client imposta la retention esplicitamente',
      retentionGovernance: 'Governance (bypass privilegiato possibile)',
      retentionCompliance: 'Compliance (nessun bypass fino alla scadenza)',
      retentionAmount: 'Durata',
      retentionUnit: 'Unità',
      retentionDays: 'giorni',
      retentionYears: 'anni',
      editTitle: 'Modifica il bucket {name}',
      quotaEditHint: 'Vuota = rimuove la quota (illimitata). Solo Ceph — MultiSCSI rifiuta la quota (seam datato)',
      versioningEditHint:
        'Solo le transizioni S3: mai→attivo, attivo→sospeso, sospeso→attivo — il ritorno a mai non esiste sul filo',
      versioningLockedHint: 'Questo bucket è lock-enabled: il versioning non può mai essere sospeso (la regola AWS)',
      versioningNever: 'Mai attivato',
      versioningStates: {
        enabled: 'Attivo',
        suspended: 'Sospeso (le versioni esistenti restano)'
      },
      versioningBadge: 'versionato',
      versioningSuspendedBadge: 'versioning sospeso',
      lockBadge: 'LOCK',
      lockNoDefault:
        'Lock attivo, nessuna retention di default — gli oggetti si bloccano solo quando un client imposta la retention esplicitamente',
      retentionEditHint: 'Aggiungi, cambia o rimuovi il default — la presenza del lock è una proprietà di nascita',
      usage: 'Utilizzo',
      objectsSuffix: 'oggetti',
      empty: 'Ancora nessun bucket gestito',
      wireNote:
        "Chi detiene un account può creare bucket anche via protocollo S3 — la piattaforma non forza l'esclusività: i bucket nati sul filo compaiono sotto come non gestiti, mai come righe nostre.",
      delete: {
        title: 'Eliminare il bucket {name}?',
        warning:
          'Il bucket lascia la zona — e lo spazio dei nomi resta condiviso: un client S3 può ricreare il nome subito dopo.',
        deleteObjectsLabel: 'Elimina anche gli oggetti che contiene',
        purgeWarning: 'Il task ELIMINA ogni oggetto insieme al bucket — irrecuperabile.',
        keepHint:
          "Viene eliminato solo un bucket VUOTO — uno il cui censimento mostra oggetti viene rifiutato, e l'esecutore ricontrolla sulla zona viva."
      },
      unmanagedTitle: 'Bucket non gestiti (osservati)',
      unmanagedHint:
        'Nati via protocollo S3 da chi detiene un account — osservati onestamente, mai posseduti: gestiscili o eliminali con un client S3 e le chiavi del proprietario.',
      unmanagedOwner: 'Proprietario (uid del gateway)'
    },
    accounts: {
      title: 'Account S3',
      createTitle: 'Crea account S3',
      name: 'Nome',
      nameHint: "Minuscole, cifre e trattini — immutabile: l'identità sul gateway è coniata da qui",
      accessKeyId: 'Access key ID',
      notMinted: 'Non ancora coniata',
      empty: 'Ancora nessun account S3',
      createHint:
        'La piattaforma conia la coppia di chiavi e la sigilla solo-scrittura nel keystore — la secret compare UNA volta, nella prossima schermata, e mai più.',
      secretTitle: 'Coppia di chiavi — mostrata una volta',
      secretAccessKey: 'Secret access key',
      secretShownOnce:
        'Mostrata una volta — il keystore la conserva solo-scrittura e niente sulla piattaforma la visualizzerà mai più. Perderla significa Ruotare.',
      rotate: 'Ruota la coppia di chiavi',
      rotateTitle: 'Ruota la coppia di chiavi di {name}',
      rotateHint:
        'Conia una coppia NUOVA — access key id E secret — resa una volta nella prossima schermata. Prima la nuova, poi la vecchia: la vecchia chiave continua ad autenticare per la finestra di grazia, poi un task figlio la ritira.',
      graceMinutes: 'Finestra di grazia (minuti)',
      graceHint:
        '0–1440 — per quanto la VECCHIA chiave continua a funzionare dopo che la nuova è viva (0 = scambio immediato)',
      delete: {
        title: "Eliminare l'account S3 {name}?",
        warning:
          "L'identità sul gateway se ne va e le sue chiavi smettono di autenticare immediatamente; la credenziale coniata nel keystore viene rimossa con lui. Un account che possiede ancora bucket gestiti viene rifiutato — elimina prima quei bucket."
      }
    }
  }
}
